/**
 * PasswordInput - a password box with a way to see what you typed
 * ----------------------------------------------------------------
 * Every password field on the site goes through this, so the eye button
 * behaves the same everywhere and nobody has to remember to add it.
 *
 * WHY IT IS WORTH HAVING, given a hidden password is the safer default:
 *
 * - **A password you cannot read is a password you get wrong twice.** On a
 *   phone keyboard, with autocorrect off and a number required, the commonest
 *   failure at signup is a typo the person cannot see. They then cannot log in
 *   and do not know why, which on this site means a student who never starts.
 *
 * - **It matters most where it is being CHOSEN**, not where it is being
 *   recalled - signing up, and setting a new one after a reset. Somebody who
 *   has just been through "I forgot my password" is exactly the person who
 *   needs to see the new one before committing to it.
 *
 * Three things that are deliberate:
 *
 * - **It starts hidden, always.** Showing by default would put a password on
 *   screen in a classroom or on a bus without anybody choosing that.
 * - **It never persists.** Reveal is per field and per visit; there is no
 *   setting, so it cannot be left on by accident on a shared laptop.
 * - **The button is a real <button> with an aria-label**, not an icon glued to
 *   the corner. It is reachable by keyboard, it announces which state it is
 *   in, and it is inside the tab order where somebody expects it.
 *
 * The type flips between 'password' and 'text' rather than using a CSS trick,
 * because that is the only version a password manager reads correctly.
 */

import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

import { useI18n } from '../hooks/useI18n';

export default function PasswordInput({
  id,
  name = 'password',
  value,
  onChange,
  autoComplete = 'current-password',
  required = false,
  placeholder,
  minLength,
  ...rest
}) {
  const { t } = useI18n();
  const [shown, setShown] = useState(false);

  return (
    <div className="password-field">
      <input
        id={id}
        name={name}
        type={shown ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        required={required}
        placeholder={placeholder}
        minLength={minLength}
        {...rest}
      />
      <button
        type="button"
        className="password-toggle"
        onClick={() => setShown((v) => !v)}
        /* The icons are decorative, so without this the button would have no
           name at all for a screen reader. It also says which state pressing
           it will produce, which is the useful half. */
        aria-label={shown ? t('form.hidePassword') : t('form.showPassword')}
        title={shown ? t('form.hidePassword') : t('form.showPassword')}
        /* Keeps it out of the tab order between the box and the next field?
           No - deliberately IN it. Somebody who cannot see what they typed is
           often the person using a keyboard rather than a mouse. */
      >
        {shown ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
      </button>
    </div>
  );
}
